import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Appearance, ColorSchemeName } from 'react-native';
import { getItem, setItem } from '../utils/storage';

export type ThemeName = 'system' | 'light' | 'dark';
export type FontSizeName = 'small' | 'default' | 'large';

export type Theme = {
  name: 'light' | 'dark';
  isDark: boolean;
  colors: {
    background: string;
    card: string;
    cardSecondary: string;
    text: string;
    textMuted: string;
    border: string;
    primary: string;
    primaryText: string;
    danger: string;
    success: string;
    overlay: string;
    input: string;
    messageBubble: string;
    myMessageText: string;
  };
};

export type FontSizeScale = {
  small: number;
  default: number;
  large: number;
  xlarge: number;
};

const fontSizeScales: Record<FontSizeName, FontSizeScale> = {
  small: {
    small: 11,
    default: 14,
    large: 16,
    xlarge: 18,
  },
  default: {
    small: 13,
    default: 16,
    large: 18,
    xlarge: 20,
  },
  large: {
    small: 15,
    default: 18,
    large: 20,
    xlarge: 22,
  },
};

const lightTheme: Theme = {
  name: 'light',
  isDark: false,
  colors: {
    background: '#F2F2F7',
    card: '#FFFFFF',
    cardSecondary: '#FAFAFA',
    text: '#0F1115',
    textMuted: '#6E6E73',
    border: '#D1D1D6',
    primary: '#00B900',
    primaryText: '#FFFFFF',
    danger: '#FF3B30',
    success: '#34C759',
    overlay: 'rgba(0,0,0,0.35)',
    input: '#FFFFFF',
    messageBubble: '#85E249',
    myMessageText: '#000000',
  },
};

const darkTheme: Theme = {
  name: 'dark',
  isDark: true,
  colors: {
    background: '#1a1d21',
    card: '#2c2f33',
    cardSecondary: '#3a3f45',
    text: '#FFFFFF',
    textMuted: '#8E919A',
    border: '#3a3f45',
    primary: '#00B900',
    primaryText: '#FFFFFF',
    danger: '#FF453A',
    success: '#30D158',
    overlay: 'rgba(0,0,0,0.6)',
    input: '#1a1d21',
    messageBubble: '#2c2f33',
    myMessageText: '#000000ff',
  },
};

const THEME_PREF_KEY = 'theme_preference_v1';
const FONT_SIZE_PREF_KEY = 'font_size_preference_v1';
const THEME_COLOR_KEY = 'theme_primary_color';

type ThemeContextValue = {
  theme: Theme;
  themeName: ThemeName;
  systemScheme: ColorSchemeName;
  setThemeName: (name: ThemeName) => void;
  setPrimaryColor: (color: string) => void;
};

type FontSizeContextValue = {
  fontSizeName: FontSizeName;
  fontSizeScale: FontSizeScale;
  setFontSizeName: (name: FontSizeName) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);
const FontSizeContext = createContext<FontSizeContextValue | undefined>(undefined);

const resolveTheme = (name: ThemeName, systemScheme: ColorSchemeName): Theme => {
  const mode = name === 'system' ? systemScheme || 'light' : name;
  return mode === 'dark' ? darkTheme : lightTheme;
};

const getMessageBubbleColor = (primaryColor: string, isDark: boolean) => {
  // Always use the light/theme color for my bubbles as requested, even in dark mode
  if (primaryColor === '#00B900') {
    return '#85E249';
  }
  
  const hex = primaryColor.replace('#', '');
  if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    const lr = Math.min(255, Math.floor(r + (255 - r) * 0.7));
    const lg = Math.min(255, Math.floor(g + (255 - g) * 0.7));
    const lb = Math.min(255, Math.floor(b + (255 - b) * 0.7));
    
    return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
  }
  
  return '#85E249';
};

const getMyMessageTextColor = (bubbleColor: string) => {
  const hex = bubbleColor.replace('#', '');
  if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? '#000000' : '#FFFFFF';
  }
  return '#000000';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeName, setThemeNameState] = useState<ThemeName>('system');
  const [systemScheme, setSystemScheme] = useState<ColorSchemeName>(Appearance.getColorScheme());
  const [primaryColor, setPrimaryColor] = useState<string>('#00B900');

  useEffect(() => {
    const load = async () => {
      const stored = await getItem(THEME_PREF_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        setThemeNameState(stored);
      }
      const storedColor = await getItem(THEME_COLOR_KEY);
      if (storedColor) {
        setPrimaryColor(storedColor);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const sub = Appearance.addChangeListener(({ colorScheme }) => setSystemScheme(colorScheme));
    return () => sub.remove();
  }, []);

  const setThemeName = (name: ThemeName) => {
    setThemeNameState(name);
    setItem(THEME_PREF_KEY, name).catch(() => {});
  };

  const theme = useMemo(() => {
    const baseTheme = resolveTheme(themeName, systemScheme);
    return {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        primary: primaryColor,
        messageBubble: getMessageBubbleColor(primaryColor, baseTheme.isDark),
      },
    };
  }, [themeName, systemScheme, primaryColor]);

  const setPrimaryColorAndSave = (color: string) => {
    setPrimaryColor(color);
    setItem(THEME_COLOR_KEY, color).catch(() => {});
  };

  const value = useMemo(
    () => ({ theme, themeName, systemScheme, setThemeName, setPrimaryColor: setPrimaryColorAndSave }),
    [theme, themeName, systemScheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const FontSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fontSizeName, setFontSizeNameState] = useState<FontSizeName>('default');

  useEffect(() => {
    const load = async () => {
      const stored = await getItem(FONT_SIZE_PREF_KEY);
      if (stored === 'small' || stored === 'default' || stored === 'large') {
        setFontSizeNameState(stored);
      }
    };
    load();
  }, []);

  const setFontSizeName = (name: FontSizeName) => {
    setFontSizeNameState(name);
    setItem(FONT_SIZE_PREF_KEY, name).catch(() => {});
  };

  const fontSizeScale = useMemo(() => fontSizeScales[fontSizeName], [fontSizeName]);

  const value = useMemo(
    () => ({ fontSizeName, fontSizeScale, setFontSizeName }),
    [fontSizeName, fontSizeScale]
  );

  return <FontSizeContext.Provider value={value}>{children}</FontSizeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const useFontSize = () => {
  const ctx = useContext(FontSizeContext);
  if (!ctx) throw new Error('useFontSize must be used within FontSizeProvider');
  return ctx;
};

export const useThemedStyles = <T,>(factory: (theme: Theme) => T): T => {
  const { theme } = useTheme();
  return useMemo(() => factory(theme), [theme, factory]);
};

export const themes = { light: lightTheme, dark: darkTheme };
